import { transformDocumentNumber } from '@/components/custom/erp';
import { isValidUUID } from '@/lib/uuid-validator';
import { FormFieldState } from '../hooks';

export const transformDetailRows = (detailRows: BanHangRecord[]) => {
  return detailRows.map((row: BanHangRecord, index: number) => ({
    line: index + 1,
    ...(isValidUUID(row.uuid) ? { uuid: row.uuid } : {}),
    ma_vt: row.ma_vt_data?.uuid || row.ma_vt || null,
    ten_vt: row.ma_vt_data?.ten_vt || row.ten_vt || null,
    dvt: row.dvt || row.ma_vt_data?.dvt || null,
    ma_kho: row.ma_kho_data?.uuid || row.ma_kho || null,
    ma_vi_tri: row.ma_vi_tri_data?.uuid || row.ma_vi_tri || null,

    so_luong: Number(row.so_luong || 0),
    ct_km: Number(row.ct_km || 0),
    pn_tb: row.pn_tb || 0,
    gia_nt: Number(row.gia_nt || 0),
    tien_nt: Number(row.tien_nt || 0),
    gia_nt1: Number(row.gia_nt1 || 0),
    gia_nt2: Number(row.gia_nt2 || 0),
    tien_nt2: Number(row.tien_nt2 || 0),
    tl_ck: Number(row.tl_ck || 0),
    ck_nt: Number(row.ck_nt || 0),
    ma_thue: row.ma_thue_data?.uuid || row.ma_thue || null,
    tk_thue_no: row.tk_thue_no_data?.uuid || row.tk_thue_no || null,
    thue_suat: row.thue_suat || 0,
    thue_nt: roundNumber(row.thue_nt),

    tk_dt: row.tk_dt_data?.uuid || row.tk_dt || null,
    tk_gv: row.tk_gv_data?.uuid || row.tk_gv || null,
    tk_vt: row.tk_vt_data?.uuid || row.tk_vt || null,
    tk_ck: row.tk_ck_data?.uuid || row.tk_ck || null,
    tk_km: row.tk_km_data?.uuid || row.tk_km || null,

    ma_bp: row.ma_bp_data?.uuid || row.ma_bp || null,
    ma_vv: row.ma_vv_data?.uuid || row.ma_vv || null,
    ma_hd: row.ma_hd_data?.uuid || row.ma_hd || null,
    ma_dtt: row.ma_dtt_data?.uuid || row.ma_dtt || null,
    ma_ku: row.ma_ku_data?.uuid || row.ma_ku || null,
    ma_phi: row.ma_phi_data?.uuid || row.ma_phi || null,
    ma_sp: row.ma_sp_data?.uuid || row.ma_sp || null,
    ma_lsx: row.ma_lsx_data?.uuid || row.ma_lsx || null,
    ma_cp0: row.ma_cp0_data?.uuid || row.ma_cp0 || null,

    id_hd: row.id_hd?.uuid || null,
    line_hd: row.line_hd?.uuid || null
  }));
};

export const transformFormData = (
  data: BanHangPayload,
  state: FormFieldState,
  detailRows: BanHangRecord[],
  entityUnit: BanHangRecord = undefined,
  calcTotals: BanHangRecord
) => {
  // Transform detail rows
  const detail = transformDetailRows(detailRows);
  const { tongSoLuong, tongTienNt, tongTienNt2, tongChietKhau, tongThue, tongThanhToan } = calcTotals;

  return {
    ...data,
    unit_id: entityUnit?.uuid || null,
    ty_gia: data.ty_gia ? parseFloat(data.ty_gia).toFixed(2) : 1.0,

    ma_kh: state.khachHang?.uuid || null,
    ma_so_thue: data.ma_so_thue || state.khachHang?.tax_code || null,
    dia_chi: data.dia_chi || state.khachHang?.address || null,
    ong_ba: data.ong_ba || state.khachHang?.contact_person || null,
    e_mail: data.e_mail || state.khachHang?.email || null,
    dien_giai: data.dien_giai || state.khachHang?.description || null,
    ngay_ct: data.ngay_ct || new Date().toISOString().split('T')[0],
    ngay_lct: data.ngay_ct,

    // References
    ma_nvbh: state.nhanVien?.uuid || state.khachHang?.sales_rep_data?.uuid || null,
    tk: state.taiKhoan?.uuid || state.khachHang?.account_data?.uuid || null,
    ma_mau_ct: state.mauHoaDon?.uuid || null,
    ma_tt: state.hanThanhToan?.uuid || state.khachHang?.payment_term_data?.uuid || null,
    ma_nt: data.ma_nt || null,
    ...transformDocumentNumber(state.quyenChungTu, state.soChungTu),
    ma_dc: state.diaChi?.uuid || null,
    ma_ptvc: state.phuongTienVanChuyen?.uuid || null,
    ma_ptgh: state.phuongThucGiaoHang?.uuid || null,
    ma_kh9: state.cucThue?.uuid || null,

    // E-invoice information
    so_ct_hddt: data.so_ct_hddt || null,
    ngay_ct_hddt: data.ngay_ct_hddt || null,
    so_ct2_hddt: data.so_ct2_hddt || null,
    ma_mau_ct_hddt: data.ma_mau_ct_hddt || null,
    ma_tthddt: data.ma_tthddt || '0',
    ma_pttt: data.ma_pttt || null,

    // Status
    status: data.status || '5',
    t_tien_nt: roundNumber(tongTienNt2),
    t_tien: roundNumber(tongTienNt2),
    t_ck_nt: roundNumber(tongChietKhau),
    t_ck: roundNumber(tongChietKhau),
    t_tc_tien_nt2: roundNumber(tongTienNt),
    t_tc_tien: roundNumber(tongTienNt),
    t_km_nt: 0,
    t_km: 0,
    t_so_luong: roundNumber(tongSoLuong),
    t_thue_nt: roundNumber(tongThue),
    t_thue: roundNumber(tongThue),
    t_tt_nt: roundNumber(tongThanhToan),
    t_tt: roundNumber(tongThanhToan),

    // Detail arrays
    chi_tiet_items: detail
  };
};

const roundNumber = (value: BanHangValue) => {
  const numericValue = Number(value || 0);
  return Number.isNaN(numericValue) ? 0 : Number(numericValue.toFixed(2));
};
